import { getErrorMessage, getErrorReason } from '@ms/yammer-telemetry';
import { flushSync } from 'react-dom';

import type { PromiseThunkAction } from '../types';

import cachedApiAction from './cachedApiAction';
import type { GraphqlApiError } from './errors';
import { endManagedApiMeasurement, getApiMeasureName, startManagedApiMeasurement } from './performance';
import type { ManagedApiActionOptions } from './types';

interface ManagedApiActionResult {
  readonly cacheHit: boolean | undefined;
}

const managedApiAction = <TResponse>(
  options: ManagedApiActionOptions<TResponse>
): PromiseThunkAction<ManagedApiActionResult> => {
  const {
    lifecycle: { pending, fulfilled, rejected },
  } = options;

  return async (dispatch) => {
    try {
      const pendingAction = pending();

      // Temporary workaround to support React 18. React 18 batches state updates inside promises, so areas of our existing
      // code that rely on sequential state updates, like with useDidPublishActionJustComplete.ts break. The
      // `flushSyncLifecycleActions` option was added to selectively disable batching for these specific cases
      // React 18 change: https://react.dev/blog/2022/03/08/react-18-upgrade-guide#automatic-batching
      // Tech debt ticket: https://dev.azure.com/yammer/engineering/_workitems/edit/382827
      if (options.flushSyncLifecycleActions) {
        flushSync(() => {
          dispatch(pendingAction);
        });
      } else {
        dispatch(pendingAction);
      }

      const coreAction = cachedApiAction<TResponse>(options);
      const { response, cacheHit } = await dispatch(coreAction);
      const fulfilledAction = fulfilled(response);

      if (options.flushSyncLifecycleActions) {
        flushSync(() => {
          dispatch(fulfilledAction);
        });
      } else {
        dispatch(fulfilledAction);
      }

      return { cacheHit };
    } catch (err) {
      const rejectedAction = rejected(err);
      dispatch(rejectedAction);
      throw err;
    }
  };
};

type PerformanceReportedManagedApiAction = <TResponse>(
  options: ManagedApiActionOptions<TResponse>
) => PromiseThunkAction<void>;
const performanceReportedManagedApiAction: PerformanceReportedManagedApiAction = (options) => async (dispatch) => {
  const { request, telemetry, caching } = options;
  const apiMeasureName = getApiMeasureName(request);
  const measure = startManagedApiMeasurement(apiMeasureName);
  let requestRejected = false;
  let error: Error | GraphqlApiError | undefined;
  let cacheHit;

  try {
    ({ cacheHit } = await dispatch(managedApiAction(options)));
  } catch (err) {
    requestRejected = true;
    error = err;
    throw err;
  } finally {
    endManagedApiMeasurement({
      ...measure,
      eventProperties: {
        ...measure.eventProperties,
        apiName: apiMeasureName,
        actionName: telemetry?.actionName,
        actionType: telemetry?.actionType,
        cacheExists: telemetry?.cacheExists,
        cachingStrategy: caching?.strategy,
        endedWithError: requestRejected,
        message: getErrorMessage(error),
        reason: getErrorReason(error),
        idempotencyToken: telemetry?.idempotencyToken,
        requestId: '',
        initiator: request.initiator,
        cacheHit,
        ...telemetry?.customProperties,
      },
    });
  }
};

export default performanceReportedManagedApiAction;
